import { InfiniteData } from '@tanstack/react-query'

type PaginatedList<D> = {
  items: D[]
  next: string
}

export const updateListCachesByCreation =
  <D, RD extends D>(data: RD, variables: { idKey: keyof D; originId?: string }) =>
  (old?: InfiniteData<PaginatedList<D>>) => {
    if (old) {
      if (!old.pages.length) {
        return {
          ...old,
          pages: [
            {
              items: [data],
              next: '',
            },
          ],
        }
      }

      if (variables.originId) {
        const pagesReducedToIndices = old.pages.map((page) =>
          page.items.findIndex((item) => item[variables.idKey] === variables.originId),
        )
        const pageIdxOfItem = pagesReducedToIndices.findIndex((pageIdx) => pageIdx > -1)
        if (pageIdxOfItem > -1) {
          // Item does exist
          const pages = [...old.pages]

          pages[pageIdxOfItem].items.splice(pagesReducedToIndices[pageIdxOfItem] + 1, 0, data)

          return {
            ...old,
            pages,
          } satisfies typeof old
        }
      }

      const pages = [...old.pages]

      pages[pages.length - 1].items.push(data)

      return {
        ...old,
        pages,
      } satisfies typeof old
    }
    return old
  }

export const updateListCachesByUpdate =
  <D, RD extends D>(data: RD, variables: { idKey: keyof D; idToMatch: string }) =>
  (old?: InfiniteData<PaginatedList<D>>) => {
    if (old) {
      const pagesReducedToIndices = old.pages.map((page) =>
        page.items.findIndex((item) => item[variables.idKey] === variables.idToMatch),
      )
      const pageIdxOfItem = pagesReducedToIndices.findIndex((pageIdx) => pageIdx > -1)
      if (pageIdxOfItem > -1) {
        // Item does exist
        const pages = [...old.pages]

        pages[pageIdxOfItem].items.splice(pagesReducedToIndices[pageIdxOfItem], 1, data)
        return {
          ...old,
          pages,
        } satisfies typeof old
      }
    }
    return old
  }

export const updateListCachesByDeletion =
  <D>(variables: { idKey: keyof D; idToMatch: string }) =>
  (old?: InfiniteData<PaginatedList<D>>) => {
    if (old) {
      return {
        ...old,
        pages:
          old.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item[variables.idKey] !== variables.idToMatch),
          })) ?? [],
      }
    }
    return old
  }
